import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import {
  canManageAdminWorkspace,
  createAdminUser,
  getAdminUsers,
  isRoleElevationBlocked,
} from '../../lib/services/adminUsers'

export function AdminUsersListPage() {
  const { viewerContext } = useAuth()
  const role = viewerContext?.role || 'Surveyor'
  const canManage = canManageAdminWorkspace(role)
  const isSuperadmin = viewerContext?.is_superadmin === true
  const queryClient = useQueryClient()

  const [createEmail, setCreateEmail] = useState('')
  const [createRole, setCreateRole] = useState<'user' | 'editor' | 'admin' | 'superadmin'>('user')
  const [createPassword, setCreatePassword] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: getAdminUsers,
    enabled: canManage,
  })

  const createUserMutation = useMutation({
    mutationFn: createAdminUser,
    onSuccess: async () => {
      setCreateEmail('')
      setCreateRole('user')
      setCreatePassword('')
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
  })

  const createRoleBlocked = !isSuperadmin && isRoleElevationBlocked(role, 'user', createRole)

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Admin workspace is restricted to Admin role.
      </div>
    )
  }

  const users = data ?? []

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Admin Workspace</h1>
        <p className="mt-1 text-sm text-slate-600">Manage users, roles, and facility assignments.</p>
      </header>

      <section className="v11-section p-5">
        <h2 className="text-sm font-semibold text-slate-900">Create User</h2>
        <p className="mt-1 text-xs text-slate-500">Create tenant users for onboarding and assignment workflows.</p>
        <form
          className="mt-4 grid gap-3 md:grid-cols-4"
          onSubmit={async (event) => {
            event.preventDefault()
            await createUserMutation.mutateAsync({
              email: createEmail.trim(),
              role: createRole,
              temporary_password: createPassword,
            })
          }}
        >
          <input
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            placeholder="email@example.com"
            value={createEmail}
            onChange={(event) => setCreateEmail(event.target.value)}
            required
          />
          <select
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={createRole}
            onChange={(event) => setCreateRole(event.target.value as 'user' | 'editor' | 'admin' | 'superadmin')}
          >
            {(['user', 'editor', 'admin', 'superadmin'] as const).map((roleOption) => {
              const blocked = !isSuperadmin && isRoleElevationBlocked(role, 'user', roleOption)
              return (
                <option key={roleOption} value={roleOption} disabled={blocked}>
                  {roleOption}
                </option>
              )
            })}
          </select>
          <input
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            placeholder="Temporary password"
            type="password"
            minLength={8}
            value={createPassword}
            onChange={(event) => setCreatePassword(event.target.value)}
            required
          />
          <button
            type="submit"
            className="btn-primary"
            disabled={createUserMutation.isPending || createRoleBlocked}
          >
            {createUserMutation.isPending ? 'Creating...' : 'Create user'}
          </button>
        </form>
        {createRoleBlocked && (
          <p className="mt-2 text-xs text-slate-500">Unauthorized role elevation attempts are blocked.</p>
        )}
        {createUserMutation.error && (
          <p className="mt-2 text-sm text-red-600">{(createUserMutation.error as Error).message}</p>
        )}
      </section>

      <section className="v11-section p-5">
        {isLoading && <p className="text-sm text-slate-500">Loading users...</p>}
        {error && <p className="text-sm text-red-600">{(error as Error).message}</p>}
        {!isLoading && !error && users.length === 0 && <p className="text-sm text-slate-500">No users found.</p>}

        <div className="grid gap-3">
          {users.map((user) => (
            <Link
              key={user.user_id}
              to={`/admin/users/${user.user_id}`}
              className="v11-dashboard-card p-4"
            >
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-bold text-slate-900">{user.email}</p>
                <span className={user.role === 'admin' || user.role === 'superadmin' ? 'badge-released' : 'badge-in-progress'}>
                  {user.role}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  )
}
